import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import useUsuarios from './useUsuarios';
import useWebSocketData from './useWebSocketData';
import useRealtimeWaitTime from './useRealtimeWaitTime';
import useSidebar from './useSidebar';
import socketService from '../services/socketService';
import api from '../services/api';
import notificationManager from '../utils/notificationManager';

// Função auxiliar para comparação profunda de arrays de senhas
const compararArraysSenhas = (array1, array2) => {
  if (array1.length !== array2.length) return false;
  
  return array1.every((item1, index) => {
    const item2 = array2[index];
    return (
      item1.id === item2.id &&
      item1.numero === item2.numero &&
      item1.tipo === item2.tipo &&
      item1.status === item2.status &&
      item1.preferencial === item2.preferencial &&
      item1.servicoId === item2.servicoId
    );
  });
};

// Função auxiliar para comparação de estatísticas
const compararEstatisticas = (stats1, stats2) => {
  return (
    stats1.senhasAtivas === stats2.senhasAtivas &&
    stats1.tempoMedioAtendimento === stats2.tempoMedioAtendimento &&
    stats1.naoComparecimentos === stats2.naoComparecimentos &&
    stats1.atendimentosHoje === stats2.atendimentosHoje
  );
};

const useDashboard = () => {
  const { user, updateUser } = useAuth();
  const { atualizarGuicheUsuario } = useUsuarios();
  
  // Hook da sidebar com persistência no localStorage
  const { sidebarOpen, setSidebarOpen, toggleSidebar } = useSidebar();
  
  // Hook WebSocket para atualização automática da fila
  const {
    conectado: webSocketConectado,
    filaSenhas,
    ultimaAtualizacao: ultimaAtualizacaoWS
  } = useWebSocketData({
    fila: true,
    senhas: false,
    estatisticas: false,
    usuario: false
  });
  
  // Estados principais
  const [senhaChamada, setSenhaChamada] = useState(null);
  const [atendimentoIniciado, setAtendimentoIniciado] = useState(false);
  const [atendimentoFinalizado, setAtendimentoFinalizado] = useState(false);
  const [senhasProcessadas, setSenhasProcessadas] = useState([]);
  const [guicheSelecionado, setGuicheSelecionado] = useState(user?.guiche || 1);
  
  // Estados para diálogos
  const [dialogoAberto, setDialogoAberto] = useState(false);
  const [dialogoReabrirSenhaAberto, setDialogoReabrirSenhaAberto] = useState(false);
  const [dialogoChamarSenhaEspecifica, setDialogoChamarSenhaEspecifica] = useState(false);
  const [dialogoAlterarServicoAberto, setDialogoAlterarServicoAberto] = useState(false);
  const [dialogoConfirmarAlteracaoAberto, setDialogoConfirmarAlteracaoAberto] = useState(false);
  const [modalEmitirSenhaAberto, setModalEmitirSenhaAberto] = useState(false);
  const [modalAdicionarServicosAberto, setModalAdicionarServicosAberto] = useState(false);
  const [senhaSelecionada, setSenhaSelecionada] = useState(null);
  
  // Estados para controlar o fluxo da alteração de serviço
  const [acaoEscolhida, setAcaoEscolhida] = useState(null); // 'fila' ou 'atendente'
  const [atendenteEscolhido, setAtendenteEscolhido] = useState(null);
  
  // Estados da fila
  const [proximasSenhas, setProximasSenhas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  
  // Estados do timer
  const [tempoAtendimento, setTempoAtendimento] = useState(0);
  const timerRef = useRef(null);
  
  // Estados de animação e controle
  const [rechamando, setRechamando] = useState(false);
  const [chamadaEmAndamento, setChamadaEmAndamento] = useState(false);
  const [tamanhoDaFila, setTamanhoDaFila] = useState(0);
  const [aguardandoChamada, setAguardandoChamada] = useState(false);
  
  // Estados de estatísticas
  const [estatisticas, setEstatisticas] = useState({
    senhasAtivas: 0,
    tempoMedioAtendimento: '0 min',
    naoComparecimentos: 0,
    atendimentosHoje: 0
  });
  const [carregandoEstatisticas, setCarregandoEstatisticas] = useState(true);
  const [senhasNaFilaAtendente, setSenhasNaFilaAtendente] = useState(0);

  // Estado para controlar o carregamento da atualização dos dados do usuário
  const [atualizandoUsuario, setAtualizandoUsuario] = useState(false);

  // Estado para aviso de aguardo na fila (SIMPLES)
  const [aguardandoNaFila, setAguardandoNaFila] = useState(false);
  const [mensagemAguardo, setMensagemAguardo] = useState('');

  // ==========================================
  // NOVO: Estado para indicador visual de fila
  // ==========================================
  const [senhaAguardandoFila, setSenhaAguardandoFila] = useState(null);
  const [mostrarIndicadorFila, setMostrarIndicadorFila] = useState(false);

  // Função para calcular o tempo de espera
  const calcularTempoEspera = useCallback((dataEmissao) => {
    const agora = new Date();
    const emissao = new Date(dataEmissao);
    const diferencaMinutos = Math.floor((agora - emissao) / (1000 * 60));
    return `${diferencaMinutos} min`;
  }, []);

  // Função para salvar o estado de atendimento no localStorage
  const salvarEstadoAtendimento = useCallback((senha, iniciado, finalizado, dataInicio) => {
    if (!user || !user.id) return;
    
    if (!dataInicio) {
      try {
        const estadoSalvo = localStorage.getItem(`atendimento_${user.id}`);
        if (estadoSalvo) {
          const dadosAnteriores = JSON.parse(estadoSalvo);
          if (dadosAnteriores.iniciadoEm) {
            dataInicio = new Date(dadosAnteriores.iniciadoEm);
          }
        }
      } catch (error) {
        console.error('Erro ao ler dados salvos:', error);
      }
      
      if (!dataInicio) {
        dataInicio = new Date();
      }
    }
    
    const agora = new Date();
    const tempoDecorrido = Math.floor((agora - new Date(dataInicio)) / 1000);
    
    const dados = {
      senha,
      iniciado,
      finalizado,
      iniciadoEm: dataInicio.toISOString(),
      tempoDecorrido,
      timestamp: agora.toISOString()
    };
    
    localStorage.setItem(`atendimento_${user.id}`, JSON.stringify(dados));
  }, [user]);

  // Função para limpar o estado de atendimento no localStorage
  const limparEstadoAtendimento = useCallback(() => {
    if (!user || !user.id) return;
    localStorage.removeItem(`atendimento_${user.id}`);
  }, [user]);

  // Função para buscar a senha em atendimento do usuário no servidor
  const buscarSenhaEmAtendimento = useCallback(async () => {
    if (!user || !user.id) return;
    
    try {
      const response = await api.get(`/senhas/atendente/${user.id}/em-atendimento`);
      
      if (response.data.success && response.data.data) {
        const senhaMapeada = {
          id: response.data.data.id,
          numero: response.data.data.numero,
          tipo: response.data.data.tipo,
          status: response.data.data.status,
          tempEspera: response.data.data.tempoEspera,
          progresso: 0,
          preferencial: response.data.data.preferencial,
          emitida_em: response.data.data.emitida_em || response.data.data.chamada_em,
          servicos_adicionais: response.data.data.servicos_adicionais || []
        };
        
        // Definir estados baseados no status da senha
        const isEmAtendimento = response.data.data.status === 'EM_ATENDIMENTO';
        const isChamada = response.data.data.status === 'CHAMADA';
        
        let dataInicio;
        if (response.data.data.iniciada_em && isEmAtendimento) {
          // Se está em atendimento e tem data de início
          dataInicio = new Date(response.data.data.iniciada_em);
        } else if (response.data.data.chamada_em) {
          // Se foi chamada, usar data da chamada
          dataInicio = new Date(response.data.data.chamada_em);
        } else {
          dataInicio = new Date();
        }
        
        setSenhaChamada(senhaMapeada);
        setAtendimentoIniciado(isEmAtendimento);
        setAtendimentoFinalizado(false);
        
        // Calcular tempo apenas se está em atendimento
        if (isEmAtendimento) {
          const agora = new Date();
          const tempoDecorrido = Math.floor((agora - dataInicio) / 1000);
          setTempoAtendimento(tempoDecorrido);
        } else {
          setTempoAtendimento(0);
        }
        
        salvarEstadoAtendimento(senhaMapeada, isEmAtendimento, false, dataInicio);
        
        // CORREÇÃO: Usar notificationManager para evitar duplicações
        if (isEmAtendimento) {
          notificationManager.senhaRestaurada('atendimento', senhaMapeada.id);
        } else if (isChamada) {
          notificationManager.senhaRestaurada('chamada', senhaMapeada.id);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar senha em atendimento:', error);
    }
  }, [user, salvarEstadoAtendimento]);

  // Função para buscar senhas do backend usando ordenação por prioridade do atendente
  const buscarSenhas = useCallback(async () => {
    if (!user || !user.id) {
      console.log('Usuário não definido, não é possível buscar senhas');
      setCarregando(false);
      return;
    }

    setCarregando(true);
    try {
      const servicosResponse = await api.get('/servicos');
      
      if (!servicosResponse.data.success) {
        console.error('Erro ao buscar serviços:', servicosResponse.data.error);
        setErro('Erro ao carregar serviços');
        return;
      }
      
      const servicosMap = {};
      servicosResponse.data.data.forEach(servico => {
        servicosMap[servico.nome] = servico.id;
      });
      
      // Usar o novo endpoint que já aplica a ordenação por prioridade do atendente
      const response = await api.get(`/senhas/aguardando/atendente/${user.id}`);
      
      if (response.data.success) {
        const senhasMapeadas = response.data.data.map(senha => ({
          id: senha.id,
          numero: senha.senha,
          tipo: senha.servico_nome,
          servicoId: servicosMap[senha.servico_nome],
          status: senha.status,
          tempEspera: calcularTempoEspera(senha.emitida_em),
          progresso: 0,
          preferencial: senha.preferencial,
          emitida_em: senha.emitida_em,
          servico_alterado: senha.servico_alterado || false,
          servico_original: senha.servico_original || null,
        }));
        
        // As senhas já vêm filtradas e ordenadas do backend
        setProximasSenhas(prevSenhas => {
          if (!compararArraysSenhas(senhasMapeadas, prevSenhas)) {
            console.log('🔄 Senhas atualizadas com nova ordenação por prioridade - foram detectadas mudanças');
            return senhasMapeadas;
          } else {
            console.log('✅ Senhas não mudaram - mantendo estado atual');
            return prevSenhas;
          }
        });
        
        setErro(null);
        console.log(`✅ Senhas carregadas com nova ordenação: ${senhasMapeadas.length} senhas para atendente ${user.id}`);
      } else {
        setErro('Falha ao buscar senhas: ' + response.data.message);
      }
    } catch (error) {
      console.error('Erro ao buscar senhas:', error);
      setErro('Erro ao carregar senhas');
    } finally {
      setCarregando(false);
    }
  }, [user, calcularTempoEspera]);

  // Função para buscar estatísticas do backend (agora usa o endpoint do atendente)
  const buscarEstatisticas = useCallback(async () => {
    setCarregandoEstatisticas(true);
    try {
      // Usar o endpoint do atendente que já retorna estatísticas calculadas
      const response = await api.get(`/senhas/atendente/${user?.id || 0}`);
      
      if (response.data.success && response.data.estatisticas) {
        const { estatisticas } = response.data;
        
        const novasEstatisticas = {
          senhasAtivas: estatisticas.senhasNaFila || 0,
          tempoMedioAtendimento: estatisticas.tempoMedioAtendimento || '0 min',
          naoComparecimentos: estatisticas.naoComparecimentos || 0,
          atendimentosHoje: estatisticas.atendimentosHoje || 0
        };

        setEstatisticas(prevStats => {
          if (!compararEstatisticas(prevStats, novasEstatisticas)) {
            console.log('📊 Estatísticas específicas do atendente atualizadas - foram detectadas mudanças');
            return novasEstatisticas;
          } else {
            console.log('✅ Estatísticas específicas do atendente não mudaram - mantendo estado atual');
            return prevStats;
          }
        });
      } else {
        console.error('Erro ao buscar estatísticas do atendente:', response.data.message);
      }
    } catch (error) {
      console.error('Erro ao buscar estatísticas do atendente:', error);
    } finally {
      setCarregandoEstatisticas(false);
    }
  }, [user]);

  // Função para atualizar dados do usuário logado e recarregar senhas
  const atualizarDadosUsuario = useCallback(async () => {
    if (!user || !user.id) {
      toast.error('Nenhum usuário logado encontrado');
      return;
    }

    setAtualizandoUsuario(true);
    
    try {
      console.log('🔄 Iniciando atualização dos dados do usuário:', user.id);
      
      // Buscar dados atualizados do usuário no servidor
      const response = await api.get(`/usuarios/${user.id}`);
      
      if (response.data.success) {
        const dadosAtualizados = response.data.data;
        
        console.log('✅ Dados do usuário atualizados recebidos:', {
          id: dadosAtualizados.id,
          nome: dadosAtualizados.nome,
          servicos_atendidos: dadosAtualizados.servicos_atendidos
        });
        
        // Atualizar o contexto do usuário com os novos dados
        const sucesso = updateUser({
          servicos_atendidos: dadosAtualizados.servicos_atendidos,
          guiche: dadosAtualizados.guiche,
          // Mantemos os outros dados existentes e atualizamos apenas o que pode ter mudado
          nome: dadosAtualizados.nome,
          email: dadosAtualizados.email,
          cargo: dadosAtualizados.cargo,
          ativo: dadosAtualizados.ativo
        });
        
        if (sucesso) {
          console.log('✅ Contexto do usuário atualizado com sucesso');
          
          // Recarregar as senhas com os novos serviços atendidos
          console.log('🔄 Recarregando senhas com novos filtros...');
          await buscarSenhas();
          
          // Recarregar estatísticas também
          await buscarEstatisticas();
          
          toast.success('Dados atualizados com sucesso! Novos serviços já estão disponíveis.');
        } else {
          throw new Error('Falha ao atualizar contexto do usuário');
        }
        
      } else {
        throw new Error(response.data.error || 'Erro ao buscar dados atualizados do usuário');
      }
      
    } catch (error) {
      console.error('❌ Erro ao atualizar dados do usuário:', error);
      const mensagemErro = error.response?.data?.error || error.message || 'Erro ao atualizar dados do usuário';
      toast.error(`Erro na atualização: ${mensagemErro}`);
    } finally {
      setAtualizandoUsuario(false);
    }
  }, [user, updateUser, buscarSenhas, buscarEstatisticas]);

  // Senhas ordenadas - agora vêm pré-ordenadas do backend por prioridade do atendente
  const senhasOrdenadas = useMemo(() => {
    // As senhas já vêm ordenadas do backend com a nova lógica:
    // 1º CRITÉRIO: Prioridade do serviço para o atendente (mais alta primeiro)
    // 2º CRITÉRIO: Senhas preferenciais dentro do mesmo nível de prioridade de serviço  
    // 3º CRITÉRIO: Ordem de chegada (FIFO)
    return proximasSenhas;
  }, [proximasSenhas]);

  // Hook para atualização do tempo de espera em tempo real
  const senhasComTempoReal = useRealtimeWaitTime(senhasOrdenadas, 30000); // Atualiza a cada 30 segundos

  // Formatação do tempo
  const formatarTempo = useCallback((segundos) => {
    const minutos = Math.floor(segundos / 60);
    const segsRestantes = segundos % 60;
    return `${minutos.toString().padStart(2, '0')}:${segsRestantes.toString().padStart(2, '0')}`;
  }, []);

  // Efeito para reagir às atualizações do WebSocket
  useEffect(() => {
    if (ultimaAtualizacaoWS && filaSenhas && filaSenhas.length > 0) {
      console.log('🔄 WebSocket: Fila atualizada, aplicando dados em tempo real');
      
      // Filtrar senhas para o atendente atual
      let senhasFiltradas = [];
      const isAdmin = user && user.cargo === "ADMIN";
      const temServicosDefinidos = user && 
                                   user.servicos_atendidos && 
                                   user.servicos_atendidos.atende && 
                                   user.servicos_atendidos.atende.length > 0;
      
      if (isAdmin) {
        senhasFiltradas = filaSenhas;
      } else if (temServicosDefinidos) {
        const servicosUsuario = user.servicos_atendidos.atende.map(id => 
          typeof id === 'number' ? String(id) : id
        );
        
        senhasFiltradas = filaSenhas.filter(senha => {
          const senhaServicoId = typeof senha.servicoId === 'number' ? 
            String(senha.servicoId) : senha.servicoId;
          
          return servicosUsuario.includes(senhaServicoId);
        });
      }
      
      // Atualizar estado apenas se houver mudanças
      setProximasSenhas(prevSenhas => {
        if (!compararArraysSenhas(senhasFiltradas, prevSenhas)) {
          console.log('✅ WebSocket: Senhas da fila atualizadas com sucesso');
          return senhasFiltradas;
        }
        return prevSenhas;
      });
      
      // Atualizar contador de senhas na fila
      setSenhasNaFilaAtendente(senhasFiltradas.length);
    }
  }, [ultimaAtualizacaoWS, filaSenhas, user]);

  // Efeito para configurar listeners WebSocket específicos para a fila
  useEffect(() => {
    if (!webSocketConectado) return;

    // Listener para eventos de senha emitida
    const handleSenhaEmitida = (data) => {
      console.log('🆕 WebSocket: Nova senha emitida detectada', data);
      // Forçar atualização da fila após nova senha
      setTimeout(() => {
        buscarSenhas();
      }, 500); // Pequeno delay para garantir consistência do banco
    };

    // Listener para eventos de atualização da fila
    const handleFilaAtualizada = (data) => {
      console.log('📋 WebSocket: Fila atualizada detectada', data);
      // A atualização já é tratada pelo useWebSocketData, mas podemos adicionar lógica extra se necessário
    };

    // Listener para senhas disponíveis para chamada automática
    const handleSenhaDisponivelParaChamada = (data) => {
      console.log('🔔 WebSocket: Senha disponível para chamada automática detectada', data);
      
      // Verificar se o atendente pode atender essa senha
      const isAdmin = user && user.cargo === "ADMIN";
      const temServicosDefinidos = user && 
                                   user.servicos_atendidos && 
                                   user.servicos_atendidos.atende && 
                                   user.servicos_atendidos.atende.length > 0;
      
      let podeAtender = false;
      
      if (isAdmin) {
        podeAtender = true;
      } else if (temServicosDefinidos) {
        // Verificar se o serviço da senha está na lista de serviços do atendente
        const servicosUsuario = user.servicos_atendidos.atende;
        // Para verificar, precisaríamos do servicoId da senha, que não está no evento atual
        // Por enquanto, vamos assumir que pode atender se recebeu o evento
        podeAtender = true;
      }
      
      if (podeAtender && !chamadaEmAndamento && !senhaChamada) {
        console.log('🚀 Iniciando chamada automática da senha:', data.senha);
        // ✅ NOTIFICAÇÃO REMOVIDA: Evitar spam de notificações de senhas disponíveis
        console.log(`📋 Senha ${data.senha} está disponível para chamada`);
        
        // Atualizar a fila para garantir dados atualizados
        buscarSenhas();
      }
    };

    // Registrar listeners
    socketService.on('senhaEmitida', handleSenhaEmitida);
    socketService.on('filaAtualizada', handleFilaAtualizada);
    socketService.on('senhaAtualizada', handleSenhaEmitida); // Para compatibilidade
    socketService.on('senhaDisponivelParaChamada', handleSenhaDisponivelParaChamada);

    // Cleanup
    return () => {
      socketService.off('senhaEmitida', handleSenhaEmitida);
      socketService.off('filaAtualizada', handleFilaAtualizada);
      socketService.off('senhaAtualizada', handleSenhaEmitida);
      socketService.off('senhaDisponivelParaChamada', handleSenhaDisponivelParaChamada);
    };
  }, [webSocketConectado, buscarSenhas, user, chamadaEmAndamento, senhaChamada]);

  return {
    // Estados
    sidebarOpen,
    setSidebarOpen,
    toggleSidebar,
    senhaChamada,
    setSenhaChamada,
    atendimentoIniciado,
    setAtendimentoIniciado,
    atendimentoFinalizado,
    setAtendimentoFinalizado,
    senhasProcessadas,
    setSenhasProcessadas,
    guicheSelecionado,
    setGuicheSelecionado,
    dialogoAberto,
    setDialogoAberto,
    dialogoReabrirSenhaAberto,
    setDialogoReabrirSenhaAberto,
    dialogoChamarSenhaEspecifica,
    setDialogoChamarSenhaEspecifica,
    dialogoAlterarServicoAberto,
    setDialogoAlterarServicoAberto,
    dialogoConfirmarAlteracaoAberto,
    setDialogoConfirmarAlteracaoAberto,
    modalEmitirSenhaAberto,
    setModalEmitirSenhaAberto,
    modalAdicionarServicosAberto,
    setModalAdicionarServicosAberto,
    acaoEscolhida,
    setAcaoEscolhida,
    atendenteEscolhido,
    setAtendenteEscolhido,
    senhaSelecionada,
    setSenhaSelecionada,
    proximasSenhas,
    setProximasSenhas,
    carregando,
    erro,
    tempoAtendimento,
    setTempoAtendimento,
    timerRef,
    rechamando,
    setRechamando,
    chamadaEmAndamento,
    setChamadaEmAndamento,
    tamanhoDaFila,
    setTamanhoDaFila,
    aguardandoChamada,
    setAguardandoChamada,
    estatisticas,
    carregandoEstatisticas,
    atualizandoUsuario,
    senhasNaFilaAtendente,
    senhaAguardandoFila,
    setSenhaAguardandoFila,
    mostrarIndicadorFila,
    setMostrarIndicadorFila,
    aguardandoNaFila,
    setAguardandoNaFila,
    mensagemAguardo,
    setMensagemAguardo,
    
    // Dados computados
    senhasOrdenadas: senhasComTempoReal,
    
    // Funções
    formatarTempo,
    calcularTempoEspera,
    salvarEstadoAtendimento,
    limparEstadoAtendimento,
    buscarSenhaEmAtendimento,
    buscarSenhas,
    buscarEstatisticas,
    atualizarDadosUsuario,
    
    // Dependências
    user,
    updateUser,
    atualizarGuicheUsuario
  };
};

export default useDashboard; 